import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  metricCard: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
  metricValue: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  section: { marginTop: theme.spacing(3) },
  sectionTitle: { marginBottom: theme.spacing(1) },
  paper: { overflowX: "auto" },
}));

const MetricCard = ({ label, value, classes }) => (
  <Paper className={classes.metricCard}>
    <Typography className={classes.metricValue}>{value ?? <CircularProgress size={24} />}</Typography>
    <Typography variant="body2" className={classes.metricLabel}>{label}</Typography>
  </Paper>
);

const PLAN_COLORS = { basic: "default", pro: "primary", full: "secondary" };

const SuperAdminDashboard = () => {
  const classes = useStyles();
  const [metrics, setMetrics] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [clearTarget, setClearTarget] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  const [allowSelfRegistration, setAllowSelfRegistration] = useState(false);
  const [savingReg, setSavingReg] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/dashboard");
      setMetrics(data.metrics);
      setCompanies(data.companies);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/config");
      setAllowSelfRegistration(data.allowSelfRegistration === "true");
    } catch (_) {}
  }, []);

  const toggleSelfRegistration = async (newVal) => {
    setSavingReg(true);
    try {
      await api.put("/superadmin/config", { key: "allowSelfRegistration", value: String(newVal) });
      setAllowSelfRegistration(newVal);
      toast.success(`Registro propio ${newVal ? "habilitado" : "deshabilitado"}`);
    } catch (err) {
      toastError(err);
    }
    setSavingReg(false);
  };

  const openImpersonateDialog = async (company) => {
    setImpersonateTarget(company);
    setSelectedUserId("");
    setCompanyUsers([]);
    setLoadingUsers(true);
    try {
      const { data } = await api.get(`/superadmin/companies/${company.id}/users`);
      setCompanyUsers(data || []);
    } catch (_) {}
    setLoadingUsers(false);
  };

  const handleImpersonate = async () => {
    if (!impersonateTarget) return;
    setImpersonating(true);
    try {
      // Get a long-lived backup token (signed with refreshSecret, 7d expiry)
      // so stop-impersonation works even if the short-lived access token expires.
      const { data: backupData } = await api.get("/auth/impersonation-backup");

      const payload = selectedUserId ? { userId: selectedUserId } : {};
      const { data } = await api.post(`/superadmin/impersonate/${impersonateTarget.id}`, payload);

      localStorage.setItem("token_superadmin_backup", backupData.backupToken);
      localStorage.setItem("impersonating_company", impersonateTarget.name);
      localStorage.setItem("token", JSON.stringify(data.token));
      if (data.refreshToken) {
        // Store impersonated refresh token so startup can restore session
        // without relying solely on the jrt cookie (avoids cookie-override races)
        localStorage.setItem("impersonating_refresh_token", data.refreshToken);
      }
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      toast.info(`Entrando como ${impersonateTarget.name}...`);
      window.location.href = "/tickets";
    } catch (err) {
      toastError(err);
      setImpersonating(false);
    }
  };

  const handleClearData = async () => {
    if (!clearTarget) return;
    setClearing(true);
    try {
      await api.delete(`/superadmin/companies/${clearTarget.id}/data`);
      toast.success(`Datos de ${clearTarget.name} eliminados`);
      setClearTarget(null);
      fetchDashboard();
    } catch (err) {
      toastError(err);
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchConfig();
  }, [fetchDashboard, fetchConfig]);

  const disconnectedCompanies = companies.filter(c => c.active && c.whatsappCount > 0 && c.connectedWhatsapps === 0);
  const waDisconnectedCount = disconnectedCompanies.length;

  const METRICS = metrics ? [
    { label: "Empresas totales", value: metrics.totalCompanies },
    { label: "Empresas activas", value: metrics.activeCompanies },
    { label: "Usuarios", value: metrics.totalUsers },
    { label: "Conexiones WA", value: metrics.totalWhatsapps },
    { label: "Tickets abiertos", value: metrics.openTickets },
    { label: "Mensajes totales", value: metrics.totalMessages },
    { label: "WA Desconectados", value: waDisconnectedCount },
  ] : Array(7).fill({ label: "...", value: null });


  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>Dashboard SuperAdmin</Typography>

      {/* Alert: companies with all WhatsApps disconnected */}
      {disconnectedCompanies.length > 0 && (
        <Paper style={{ padding: "12px 16px", marginBottom: 16, backgroundColor: "#fef2f2", borderLeft: "4px solid #ef4444" }} elevation={0}>
          <Typography variant="subtitle2" style={{ color: "#b91c1c", fontWeight: 700, marginBottom: 4 }}>
            Alerta: {disconnectedCompanies.length} empresa{disconnectedCompanies.length > 1 ? "s" : ""} con WhatsApp desconectado
          </Typography>
          <Typography variant="body2" style={{ color: "#dc2626" }}>
            {disconnectedCompanies.map(c => c.name).join(", ")}
          </Typography>
        </Paper>
      )}

      {/* Metrics */}
      <Grid container spacing={2}>
        {METRICS.map((m, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <MetricCard label={m.label} value={m.value} classes={classes} />
          </Grid>
        ))}
      </Grid>

      {/* Companies table */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>Empresas</Typography>
        <Paper className={classes.paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Usuarios</TableCell>
                <TableCell>WA</TableCell>
                <TableCell>WA Conectados</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((c) => {
                const waOk = c.whatsappCount === 0 || c.connectedWhatsapps > 0;
                return (
                <TableRow key={c.id}>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>
                    <span style={{
                      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                      backgroundColor: waOk ? "#22c55e" : "#ef4444",
                      marginRight: 6, verticalAlign: "middle"
                    }} />
                    {c.name}
                  </TableCell>
                  <TableCell>
                    <Chip label={c.plan} size="small" color={PLAN_COLORS[c.plan] || "default"} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.active ? "Activa" : "Inactiva"}
                      size="small"
                      color={c.active ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>{c.userCount}</TableCell>
                  <TableCell>{c.whatsappCount}</TableCell>
                  <TableCell>{c.connectedWhatsapps}</TableCell>
                  <TableCell>
                    {c.createdAt ? format(parseISO(c.createdAt), "dd/MM/yy") : "—"}
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" color="primary"
                      onClick={() => openImpersonateDialog(c)}
                      style={{ marginRight: 6 }}>
                      Ver como
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<DeleteSweepIcon />}
                      onClick={() => setClearTarget({ id: c.id, name: c.name })}
                    >
                      Limpiar
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </div>

      {/* Global settings */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>Configuración global</Typography>
        <Paper style={{ padding: 16 }}>
          <FormControlLabel
            control={
              <Switch
                checked={allowSelfRegistration}
                onChange={e => toggleSelfRegistration(e.target.checked)}
                disabled={savingReg}
                color="primary"
              />
            }
            label="Permitir registro de empresas (signup público)"
          />
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
            Si está desactivado, /signup muestra un mensaje de "registro deshabilitado" y el endpoint rechaza nuevos registros.
          </Typography>
        </Paper>
      </div>

      {/* Impersonate dialog */}
      <Dialog open={!!impersonateTarget} onClose={() => !impersonating && setImpersonateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ver como — {impersonateTarget?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 16 }}>
            Por defecto entrás como el admin de la empresa. Podés elegir un usuario específico.
          </DialogContentText>
          {loadingUsers ? (
            <CircularProgress size={20} />
          ) : companyUsers.length > 0 ? (
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Ver como usuario</InputLabel>
              <Select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                label="Ver como usuario"
              >
                <MenuItem value=""><em>Admin de la empresa (por defecto)</em></MenuItem>
                {companyUsers.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name} — {u.profile}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpersonateTarget(null)} disabled={impersonating}>Cancelar</Button>
          <Button onClick={handleImpersonate} color="primary" variant="contained" disabled={impersonating}>
            {impersonating ? <CircularProgress size={18} /> : "Entrar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear company data dialog */}
      <Dialog open={!!clearTarget} onClose={() => setClearTarget(null)}>
        <DialogTitle>Limpiar datos de prueba</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esto eliminará todos los <strong>contactos, tickets y mensajes</strong> de <strong>{clearTarget?.name}</strong>.
            La configuración, usuarios, conexiones y colas se mantendrán.
            <br /><br />
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearTarget(null)} color="default" disabled={clearing}>Cancelar</Button>
          <Button onClick={handleClearData} color="secondary" variant="contained" disabled={clearing}>
            {clearing ? <CircularProgress size={20} /> : "Eliminar datos"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
